import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

/** Minimal typing for the globally-injected TradingView embed library (tv.js). */
type TVWidgetCtor = new (config: Record<string, unknown>) => unknown;
declare global {
  interface Window {
    TradingView?: { widget: TVWidgetCtor };
  }
}

const TV_SRC = 'https://s3.tradingview.com/tv.js';

/** Load tv.js once, shared across all chart instances. */
let scriptPromise: Promise<void> | null = null;
function loadTradingView(): Promise<void> {
  if (window.TradingView) return Promise.resolve();
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TV_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('tv.js failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = TV_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('tv.js failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

let uid = 0;

interface Props {
  /** Bare pair, e.g. "BTCUSDT". */
  symbol: string;
  /** TradingView exchange prefix, e.g. "BYBIT", "BINANCE". */
  exchange?: string;
  /** TradingView interval code: 1,3,5,15,30,60,120,240,D,W. */
  interval?: string;
  height?: number;
}

/**
 * Full TradingView Advanced Chart (the embeddable tv.js widget) — candles,
 * drawing tools, indicators, timeframes. Re-instantiates when the symbol,
 * exchange or interval changes; the library is loaded once and shared.
 */
export function TradingViewChart({ symbol, exchange = 'BYBIT', interval = '60', height = 480 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(`tv_chart_${++uid}`);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);

  // Use the browser's native Fullscreen API on the wrapper: the element becomes
  // truly viewport-sized (UA :fullscreen rules) and the autosize widget fills it
  // reliably — unlike a CSS overlay, which the TradingView iframe doesn't repaint.
  const toggleFull = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void el.requestFullscreen?.();
  };

  useEffect(() => {
    const onFsChange = () => {
      setFull(document.fullscreenElement === wrapRef.current);
      // Nudge the autosize widget to recompute against the new dimensions.
      setTimeout(() => window.dispatchEvent(new Event('resize')), 80);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadTradingView()
      .then(() => {
        if (cancelled || !containerRef.current || !window.TradingView) return;
        containerRef.current.innerHTML = ''; // clear any prior widget before rebuilding
        // eslint-disable-next-line no-new
        new window.TradingView.widget({
          container_id: idRef.current,
          symbol: `${exchange}:${symbol}`,
          interval,
          autosize: true,
          timezone: 'Asia/Bahrain',
          theme: 'dark',
          style: '1', // candles
          locale: 'en',
          toolbar_bg: '#0b1020',
          enable_publishing: false,
          allow_symbol_change: true,
          hide_side_toolbar: false,
          withdateranges: true,
          save_image: false,
        });
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [symbol, exchange, interval]);

  return (
    <Box
      ref={wrapRef}
      sx={{
        position: 'relative', width: '100%', background: '#0b1020',
        height: full ? '100vh' : height,
        borderRadius: full ? 0 : 1, overflow: 'hidden',
      }}
    >
      <Tooltip title={full ? 'Exit full screen (Esc)' : 'Full screen'}>
        <IconButton
          size="small"
          onClick={toggleFull}
          sx={{
            position: 'absolute', top: 8, right: 8, zIndex: 5,
            color: '#cdd5e6', bgcolor: 'rgba(15,20,36,0.85)', border: '1px solid #243049',
            '&:hover': { bgcolor: 'rgba(20,29,51,0.95)' },
          }}
        >
          {full ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <div id={idRef.current} ref={containerRef} style={{ height: '100%', width: '100%' }} />
    </Box>
  );
}
